import { useForm, DeepKeys, type FieldApi, type ReactFormExtendedApi } from "@tanstack/react-form"
import { createContext, useContext } from "react"
import type { FormSections } from "./configuration-sections"

/**
 * Typing for the form API which defines various validation and on event actions
 * We're currently not using any except for the form data, so they're undefined;
 * this isn't the best but at the moment this seems to be the best way to type the form
 *
 * The parameters are:
 * TFormData
 * TOnMount extends undefined | FormValidateOrFn<TFormData>
 * TOnChange extends undefined | FormValidateOrFn<TFormData>
 * TOnChangeAsync extends undefined | FormAsyncValidateOrFn<TFormData>
 * TOnBlur extends undefined | FormValidateOrFn<TFormData>
 * TOnBlurAsync extends undefined | FormAsyncValidateOrFn<TFormData>
 * TOnSubmit extends undefined | FormValidateOrFn<TFormData>
 * TOnSubmitAsync extends undefined | FormAsyncValidateOrFn<TFormData>
 * TOnDynamic extends undefined | FormValidateOrFn<TFormData>
 * TOnDynamicAsync extends undefined | FormAsyncValidateOrFn<TFormData>
 * TOnServer extends undefined | FormAsyncValidateOrFn<TFormData>
 * TSubmitMeta
 */
export type OptimizeFormApi = ReactFormExtendedApi<
  FormSections,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined
>

/**
 * Typing for various form fields. Similar approach to the above
 *
 * The parameters are:
 * TParentData
 * TName
 * TData
 * TOnMount
 * TOnChange
 * TOnChangeAsync
 * TOnBlur
 * TOnBlurAsync
 * TOnSubmit
 * TOnSubmitAsync
 * TOnDynamic
 * TOnDynamicAsync
 * TFormOnMount
 * TFormOnChange
 * TFormOnChangeAsync
 * TFormOnBlur
 * TFormOnBlurAsync
 * TFormOnSubmit
 * TFormOnSubmitAsync
 * TFormOnDynamic
 * TFormOnDynamicAsync
 * TFormOnServer
 * TPatentSubmitMeta
 */
export type OptimizeFieldApi = FieldApi<
  FormSections,
  DeepKeys<FormSections>,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined
>

// Form context provider to allow child components to access the form
export const OptimizeFormContext = createContext<OptimizeFormApi | null>(null)

// Hook used by sections to access form without prop drilling
export function useFormContext(): OptimizeFormApi {
  const context = useContext(OptimizeFormContext)
  if (!context) {
    throw new Error("useFormContext must be used within form context provider")
  }
  return context
}

export function useOptimizeForm(options: {
  defaultValues: FormSections
  onSubmit: (data: { value: FormSections }) => void | Promise<void>
}): OptimizeFormApi {
  return useForm(options) as OptimizeFormApi
}
